const NQE_TAG_NAME = 'negroni-quiz';

class NegroniQuiz extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.timerId = null;
    this.redirectTimerId = null;
    this.answerAdvanceTimerId = null;
    this.endAt = 0;
    this.remainingTimeMs = 0;
    this.timerPausedForFeedback = false;
    this.questions = [];
    this.activeQuestions = [];
    this.answers = [];
    this.currentIndex = 0;
    this.score = 0;
    this.locked = false;
    this.config = {};
    this.elements = {};
  }

  connectedCallback() {
    if (this.dataset.nqeInitialized === 'true') return;
    this.dataset.nqeInitialized = 'true';
    this.abortController = new AbortController();

    try {
      this.config = this.parseJson('[data-nqe-config]');
      this.questions = this.normalizeQuestions(this.parseJson('[data-nqe-questions]'));
      this.cacheElements();
      this.configureVisibility();
      this.bindEvents();
      this.prepareIntro();
    } catch (error) {
      console.error('[NQE] Initialization failed:', error);
      this.showFatalError(error instanceof Error ? error.message : String(error));
    }
  }

  disconnectedCallback() {
    this.stopTimer();
    this.clearRedirect();
    window.clearTimeout(this.answerAdvanceTimerId);
    this.abortController?.abort();
    this.dataset.nqeInitialized = 'false';
  }

  parseJson(selector) {
    const node = this.querySelector(selector);
    if (!node) throw new Error(`Required data node not found: ${selector}`);

    try {
      return JSON.parse(node.textContent.trim() || '{}');
    } catch {
      throw new Error(`Invalid JSON in ${selector}`);
    }
  }

  cacheElements() {
    this.elements = {
      screens: [...this.querySelectorAll('[data-nqe-screen]')],
      resultScreen: this.querySelector('[data-nqe-screen="result"]'),
      questionAnchor: this.querySelector('[data-nqe-question-anchor]'),
      startButton: this.querySelector('[data-nqe-start]'),
      retryButton: this.querySelector('[data-nqe-retry]'),
      enterLink: this.querySelector('[data-nqe-enter]'),
      nextButton: this.querySelector('[data-nqe-next]'),
      counter: this.querySelector('[data-nqe-counter]'),
      timer: this.querySelector('[data-nqe-timer]'),
      progress: this.querySelector('[data-nqe-progress]'),
      progressBar: this.querySelector('[data-nqe-progress-bar]'),
      question: this.querySelector('[data-nqe-question]'),
      answers: this.querySelector('[data-nqe-answers]'),
      feedback: this.querySelector('[data-nqe-feedback]'),
      feedbackStatus: this.querySelector('[data-nqe-feedback-status]'),
      feedbackAnswer: this.querySelector('[data-nqe-feedback-answer]'),
      feedbackExplanation: this.querySelector('[data-nqe-feedback-explanation]'),
      live: this.querySelector('[data-nqe-live]'),
      score: this.querySelector('[data-nqe-score]'),
      resultTitle: this.querySelector('[data-nqe-result-title]'),
      resultMessage: this.querySelector('[data-nqe-result-message]'),
      setupMessage: this.querySelector('[data-nqe-setup-message]'),
      poolCount: this.querySelector('[data-nqe-pool-count]'),
      errorMessage: this.querySelector('[data-nqe-error-message]')
    };
  }

  configureVisibility() {
    this.elements.timer.hidden = !this.toBoolean(this.config.showTimer, true);
    this.elements.progress.hidden = !this.toBoolean(this.config.showProgress, true);
    this.elements.counter.hidden = !this.toBoolean(this.config.showQuestionNumber, true);
  }

  bindEvents() {
    const { signal } = this.abortController;
    this.elements.startButton?.addEventListener('click', () => this.start(), { signal });
    this.elements.retryButton?.addEventListener('click', () => this.start(), { signal });
    this.elements.nextButton?.addEventListener('click', () => this.goToNextQuestion(), { signal });
  }

  prepareIntro() {
    this.showScreen('intro');

    if (this.toBoolean(this.config.showPoolCount, false) && this.questions.length > 0) {
      const label = this.config.labels?.poolCount || 'QUESTION POOL';
      this.elements.poolCount.textContent = `${label}: ${this.questions.length}`;
      this.elements.poolCount.hidden = false;
    }

    if (this.questions.length === 0) {
      this.elements.startButton.disabled = true;
      this.elements.setupMessage.textContent =
        this.config.messages?.setupMessage ||
        'クイズバンクが未設定、または公開中の問題がありません。';
      this.elements.setupMessage.hidden = false;
    } else {
      this.elements.startButton.disabled = false;
      this.elements.setupMessage.hidden = true;
    }
  }

  start() {
    if (this.questions.length === 0) return;

    this.stopTimer();
    this.clearRedirect();
    window.clearTimeout(this.answerAdvanceTimerId);

    const requestedCount = this.toPositiveInteger(this.config.questionCount, 10);
    const count = Math.min(requestedCount, this.questions.length);

    this.activeQuestions = this.toBoolean(this.config.shuffleQuestions, true)
      ? this.shuffle([...this.questions]).slice(0, count)
      : [...this.questions].slice(0, count);

    this.currentIndex = 0;
    this.score = 0;
    this.answers = [];
    this.locked = false;
    this.timerPausedForFeedback = false;
    this.hideFeedback();

    if (this.questions.length < requestedCount) {
      this.announce(
        this.config.messages?.notEnoughQuestions ||
        '有効な問題数が出題設定より少ないため、登録済みの問題だけで出題します。'
      );
    }

    this.showScreen('quiz');
    this.renderQuestion();
    this.startTimer();

    this.dispatchQuizEvent('nqe:start', {
      questionCount: this.activeQuestions.length,
      poolCount: this.questions.length
    });
  }

  renderQuestion() {
    const question = this.activeQuestions[this.currentIndex];
    if (!question) {
      this.finish('complete');
      return;
    }

    this.locked = false;
    this.hideFeedback();
    this.elements.question.replaceChildren();
    this.elements.answers.replaceChildren();

    const currentNumber = this.currentIndex + 1;
    const total = this.activeQuestions.length;
    const questionLabel = this.config.labels?.question || 'QUESTION';

    this.elements.counter.textContent =
      `${questionLabel} ${String(currentNumber).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
    this.updateProgress(total > 0 ? (this.currentIndex / total) * 100 : 0);

    if (question.question) {
      const heading = document.createElement('h2');
      heading.className = 'nqe__question-text h3';
      heading.textContent = question.question;
      this.elements.question.append(heading);
    }

    if (question.questionImage?.src) {
      const figure = document.createElement('figure');
      figure.className = 'nqe__question-image';

      const image = document.createElement('img');
      image.src = question.questionImage.src;
      image.alt = question.questionImage.alt || '';
      image.loading = 'eager';
      image.decoding = 'async';

      figure.append(image);
      this.elements.question.append(figure);
    }

    const answers = this.toBoolean(this.config.shuffleAnswers, false)
      ? this.shuffle(question.answers.map((answer) => ({ ...answer })))
      : question.answers.map((answer) => ({ ...answer }));

    answers.forEach((answer, index) => {
      this.elements.answers.append(this.createAnswerButton(answer, index));
    });

    this.announce(`${questionLabel} ${currentNumber} / ${total}`);
  }

  createAnswerButton(answer, index) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'nqe__answer';
    button.dataset.answerKey = answer.key;
    button.setAttribute('aria-pressed', 'false');

    const visualLabel = document.createElement('span');
    visualLabel.className = 'nqe__answer-label';
    visualLabel.textContent = String.fromCharCode(65 + index);
    visualLabel.setAttribute('aria-hidden', 'true');
    button.append(visualLabel);

    const content = document.createElement('span');
    content.className = 'nqe__answer-content';

    if (answer.image?.src) {
      const media = document.createElement('span');
      media.className = 'nqe__answer-image';

      const image = document.createElement('img');
      image.src = answer.image.src;
      image.alt = answer.image.alt || '';
      image.loading = 'lazy';
      image.decoding = 'async';

      media.append(image);
      content.append(media);
    }

    if (answer.text) {
      const text = document.createElement('span');
      text.className = 'nqe__answer-text';
      text.textContent = answer.text;
      content.append(text);
    }

    button.append(content);
    button.addEventListener('click', () => this.selectAnswer(button, answer), {
      signal: this.abortController.signal
    });

    return button;
  }

  selectAnswer(button, answer) {
    if (this.locked) return;
    this.locked = true;

    const question = this.activeQuestions[this.currentIndex];
    const isCorrect = answer.key === question.correctAnswer;
    if (isCorrect) this.score += 1;

    this.answers.push({
      questionId: question.id,
      selectedAnswer: answer.key,
      correct: isCorrect
    });

    [...this.elements.answers.querySelectorAll('button')].forEach((answerButton) => {
      const answerKey = answerButton.dataset.answerKey;
      answerButton.disabled = true;
      answerButton.setAttribute('aria-pressed', String(answerButton === button));

      if (answerKey === question.correctAnswer) {
        answerButton.classList.add('nqe__answer--correct');
      } else if (answerButton === button) {
        answerButton.classList.add('nqe__answer--incorrect');
      }
    });

    this.dispatchQuizEvent('nqe:answer', {
      questionId: question.id,
      questionNumber: this.currentIndex + 1,
      correct: isCorrect
    });

    if (this.toBoolean(this.config.showAnswerFeedback, true)) {
      if (this.toBoolean(this.config.pauseTimerDuringFeedback, true)) {
        this.pauseTimer();
      }

      this.renderFeedback(question, isCorrect);
      return;
    }

    this.answerAdvanceTimerId = window.setTimeout(() => {
      this.advanceQuestion();
    }, 180);
  }

  renderFeedback(question, isCorrect) {
    const labels = this.config.labels || {};
    const correctAnswer = question.answers.find(
      (answer) => answer.key === question.correctAnswer
    );

    this.elements.feedbackStatus.textContent =
      isCorrect ? (labels.correct || 'CORRECT') : (labels.incorrect || 'INCORRECT');
    this.elements.feedbackStatus.classList.toggle('nqe__feedback-status--correct', isCorrect);
    this.elements.feedbackStatus.classList.toggle('nqe__feedback-status--incorrect', !isCorrect);

    this.elements.feedbackAnswer.replaceChildren();

    if (correctAnswer) {
      const heading = document.createElement('p');
      heading.className = 'nqe__feedback-heading smallcaps';
      heading.textContent = labels.correctAnswer || '正解';

      const answerContent = document.createElement('div');
      answerContent.className = 'nqe__feedback-answer-content';

      if (correctAnswer.image?.src) {
        const image = document.createElement('img');
        image.src = correctAnswer.image.src;
        image.alt = correctAnswer.image.alt || '';
        image.loading = 'lazy';
        image.decoding = 'async';
        answerContent.append(image);
      }

      if (correctAnswer.text) {
        const text = document.createElement('p');
        text.className = 'nqe__feedback-answer-text';
        text.textContent = correctAnswer.text;
        answerContent.append(text);
      }

      this.elements.feedbackAnswer.append(heading, answerContent);
    }

    this.elements.feedbackExplanation.replaceChildren();

    const showExplanationImage =
      this.toBoolean(this.config.showExplanationImage, true) &&
      Boolean(question.explanationImage?.src);

    const hasExplanation = Boolean(question.explanation);
    const hasExplanationContent = hasExplanation || showExplanationImage;

    if (hasExplanationContent) {
      const heading = document.createElement('p');
      heading.className = 'nqe__feedback-heading smallcaps';
      heading.textContent = labels.explanation || '解説';
      this.elements.feedbackExplanation.append(heading);

      const content = document.createElement('div');
      content.className = 'nqe__feedback-explanation-content';

      const imagePosition =
        this.config.explanationImagePosition === 'below' ? 'below' : 'above';

      const appendImage = () => {
        if (!showExplanationImage) return;

        const figure = document.createElement('figure');
        figure.className = 'nqe__feedback-explanation-image';
        figure.style.setProperty(
          '--nqe-explanation-image-max-width',
          `${this.toPositiveInteger(this.config.explanationImageMaxWidth, 800)}px`
        );

        const image = document.createElement('img');
        image.src = question.explanationImage.src;
        image.alt = question.explanationImage.alt || '';
        image.loading = 'lazy';
        image.decoding = 'async';

        figure.append(image);
        content.append(figure);
      };

      const appendText = () => {
        if (!hasExplanation) return;

        const text = document.createElement('p');
        text.className = 'nqe__feedback-explanation-text';
        text.textContent = question.explanation;
        content.append(text);
      };

      if (imagePosition === 'above') {
        appendImage();
        appendText();
      } else {
        appendText();
        appendImage();
      }

      this.elements.feedbackExplanation.append(content);
      this.elements.feedbackExplanation.hidden = false;
    } else {
      this.elements.feedbackExplanation.hidden = true;
    }

    const isLastQuestion = this.currentIndex >= this.activeQuestions.length - 1;
    this.elements.nextButton.textContent =
      isLastQuestion ? (labels.result || 'RESULT') : (labels.next || 'NEXT');

    this.elements.feedback.hidden = false;

    const announcement = isCorrect
      ? (labels.correct || 'CORRECT')
      : (labels.incorrect || 'INCORRECT');

    this.announce(announcement);
    this.elements.feedbackStatus.focus({ preventScroll: true });
  }

  goToNextQuestion() {
    if (this.elements.feedback.hidden) return;
    this.advanceQuestion();
  }

  advanceQuestion() {
    this.hideFeedback();
    this.currentIndex += 1;

    if (this.currentIndex >= this.activeQuestions.length) {
      this.finish('complete');
      return;
    }

    this.renderQuestion();

    if (this.timerPausedForFeedback) {
      this.resumeTimer();
    }

    this.scrollToElement(this.elements.questionAnchor);
  }

  hideFeedback() {
    if (!this.elements.feedback) return;

    this.elements.feedback.hidden = true;
    this.elements.feedbackStatus?.classList.remove(
      'nqe__feedback-status--correct',
      'nqe__feedback-status--incorrect'
    );
    this.elements.feedbackAnswer?.replaceChildren();
    this.elements.feedbackExplanation?.replaceChildren();
  }

  startTimer() {
    const seconds = this.toPositiveInteger(this.config.timeLimitSeconds, 300);
    this.runTimer(seconds * 1000);
  }

  runTimer(milliseconds) {
    this.stopTimer();
    this.remainingTimeMs = Math.max(0, milliseconds);
    this.endAt = performance.now() + this.remainingTimeMs;
    this.updateTimer(this.remainingTimeMs);

    this.timerId = window.setInterval(() => {
      this.remainingTimeMs = Math.max(0, this.endAt - performance.now());
      this.updateTimer(this.remainingTimeMs);

      if (this.remainingTimeMs <= 0) {
        this.finish('timeout');
      }
    }, 250);
  }

  pauseTimer() {
    if (!this.timerId) return;

    this.remainingTimeMs = Math.max(0, this.endAt - performance.now());
    this.stopTimer();
    this.timerPausedForFeedback = true;
  }

  resumeTimer() {
    if (!this.timerPausedForFeedback) return;

    this.timerPausedForFeedback = false;

    if (this.remainingTimeMs <= 0) {
      this.finish('timeout');
      return;
    }

    this.runTimer(this.remainingTimeMs);
  }

  updateTimer(milliseconds) {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    this.elements.timer.textContent =
      `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    this.elements.timer.classList.toggle('nqe__timer--warning', totalSeconds <= 30);
  }

  updateProgress(value) {
    const rounded = Math.max(0, Math.min(100, Math.round(value)));
    this.elements.progress.setAttribute('aria-valuenow', String(rounded));
    this.elements.progressBar.style.width = `${rounded}%`;
  }

  finish(reason) {
    this.stopTimer();
    window.clearTimeout(this.answerAdvanceTimerId);
    this.locked = true;
    this.timerPausedForFeedback = false;

    const total = this.activeQuestions.length;
    const configuredPassScore = this.toPositiveInteger(this.config.passScore, total);
    const passScore = Math.min(configuredPassScore, total);
    const passed = reason !== 'timeout' && this.score >= passScore;

    this.updateProgress(100);
    this.elements.score.textContent = `${this.score} / ${total}`;

    if (reason === 'timeout') {
      this.elements.resultTitle.textContent =
        this.config.messages?.timeoutTitle || 'TIME UP';
      this.elements.resultMessage.textContent =
        this.config.messages?.timeoutMessage ||
        '制限時間を超えました。もう一度挑戦してください。';
    } else if (passed) {
      this.elements.resultTitle.textContent =
        this.config.messages?.passTitle || 'CLEARED';
      this.elements.resultMessage.textContent =
        this.config.messages?.passMessage || 'Congratulations.';
    } else {
      this.elements.resultTitle.textContent =
        this.config.messages?.failTitle || 'NOT CLEARED';
      this.elements.resultMessage.textContent =
        this.config.messages?.failMessage ||
        '全問正解でクリアです。もう一度挑戦してください。';
    }

    const successUrl =
      typeof this.config.successUrl === 'string' ? this.config.successUrl : '#';
    const hasSuccessUrl = passed && successUrl && successUrl !== '#';

    this.elements.enterLink.hidden = !hasSuccessUrl;
    this.elements.enterLink.href = hasSuccessUrl ? successUrl : '#';
    this.elements.retryButton.hidden = false;

    if (passed) {
      try {
        sessionStorage.setItem(
          'nqe:last-pass',
          JSON.stringify({
            sectionId: this.dataset.sectionId || '',
            score: this.score,
            total,
            completedAt: new Date().toISOString()
          })
        );
      } catch {
        // The quiz remains functional when storage is unavailable.
      }
    }

    this.showScreen('result');
    this.scrollToElement(this.elements.resultScreen);
    this.elements.resultTitle.focus({ preventScroll: true });

    this.dispatchQuizEvent('nqe:complete', {
      reason,
      passed,
      score: this.score,
      total,
      answers: [...this.answers]
    });

    if (hasSuccessUrl && this.toBoolean(this.config.autoRedirect, false)) {
      const delay =
        this.toPositiveInteger(this.config.redirectDelaySeconds, 3) * 1000;
      this.redirectTimerId = window.setTimeout(
        () => window.location.assign(successUrl),
        delay
      );
    }
  }

  scrollToElement(target) {
    if (!target || !this.toBoolean(this.config.autoScrollNext, true)) return;

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    // Wait until the explanation panel has collapsed and the new question
    // has completed layout before calculating the destination.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      });
    });
  }

  showScreen(name) {
    this.elements.screens.forEach((screen) => {
      screen.hidden = screen.dataset.nqeScreen !== name;
    });
  }

  showFatalError(message) {
    try {
      this.cacheElements();
      this.showScreen('error');
      if (this.elements.errorMessage) {
        this.elements.errorMessage.textContent =
          message || 'クイズを読み込めませんでした。';
      }
    } catch {
      this.textContent = 'クイズを読み込めませんでした。';
    }
  }

  normalizeQuestions(rawQuestions) {
    if (!Array.isArray(rawQuestions)) return [];

    return rawQuestions
      .map((question, questionIndex) => {
        const answers = Array.isArray(question?.answers) ? question.answers : [];
        const normalizedAnswers = answers.slice(0, 4).map((answer, answerIndex) => ({
          key: String(
            answer?.key || String.fromCharCode(65 + answerIndex)
          ).toUpperCase(),
          text: typeof answer?.text === 'string' ? answer.text.trim() : '',
          image: {
            src: typeof answer?.image?.src === 'string'
              ? answer.image.src
              : null,
            alt: typeof answer?.image?.alt === 'string'
              ? answer.image.alt
              : ''
          }
        }));

        return {
          id: String(question?.id || `question-${questionIndex + 1}`),
          question:
            typeof question?.question === 'string'
              ? question.question.trim()
              : '',
          questionImage: {
            src:
              typeof question?.questionImage?.src === 'string'
                ? question.questionImage.src
                : null,
            alt:
              typeof question?.questionImage?.alt === 'string'
                ? question.questionImage.alt
                : ''
          },
          answers: normalizedAnswers,
          correctAnswer: String(question?.correctAnswer || '').toUpperCase(),
          explanation:
            typeof question?.explanation === 'string'
              ? question.explanation.trim()
              : '',
          explanationImage: {
            src:
              typeof question?.explanationImage?.src === 'string'
                ? question.explanationImage.src
                : null,
            alt:
              typeof question?.explanationImage?.alt === 'string'
                ? question.explanationImage.alt
                : ''
          },
          category:
            typeof question?.category === 'string' ? question.category : '',
          difficulty: Number(question?.difficulty || 0)
        };
      })
      .filter((question) => {
        const hasPrompt = Boolean(
          question.question || question.questionImage.src
        );
        const hasFourValidAnswers =
          question.answers.length === 4 &&
          question.answers.every((answer) =>
            Boolean(answer.text || answer.image.src)
          );
        const validCorrectAnswer = question.answers.some(
          (answer) => answer.key === question.correctAnswer
        );

        return hasPrompt && hasFourValidAnswers && validCorrectAnswer;
      });
  }

  shuffle(items) {
    for (let index = items.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [items[index], items[randomIndex]] = [
        items[randomIndex],
        items[index]
      ];
    }
    return items;
  }

  toPositiveInteger(value, fallback) {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  toBoolean(value, fallback = false) {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  stopTimer() {
    if (this.timerId) {
      window.clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  clearRedirect() {
    if (this.redirectTimerId) {
      window.clearTimeout(this.redirectTimerId);
      this.redirectTimerId = null;
    }
  }

  announce(message) {
    if (!this.elements.live) return;
    this.elements.live.textContent = '';
    window.requestAnimationFrame(() => {
      this.elements.live.textContent = message;
    });
  }

  dispatchQuizEvent(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail
      })
    );
  }
}

if (!customElements.get(NQE_TAG_NAME)) {
  customElements.define(NQE_TAG_NAME, NegroniQuiz);
}
